import { JwtAccessToken } from './interfaces';

/**
 * Decodes JWT payload without verifying signature (client-side claim checks only).
 */
export function parseJwtPayload(token: string | null | undefined): Partial<JwtAccessToken> | null {
  if (!token) {
    return null;
  }

  const parts: string[] = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64: string = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded: string = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json: string = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char: string) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(json) as Partial<JwtAccessToken>;
  } catch {
    return null;
  }
}

export function getJwtExpiresDate(token: string | null | undefined): Date | null {
  const payload: Partial<JwtAccessToken> | null = parseJwtPayload(token);
  if (!payload?.exp) {
    return null;
  }

  return new Date(payload.exp * 1000);
}

/**
 * User Passport access has crmClientNumber and/or anonymousPassportId.
 * preferred_username / passportId / email_verified are present on guest too — do not use as discriminator.
 */
export function isUserPassportAccessToken(accessToken: string | null | undefined): boolean {
  const payload: Partial<JwtAccessToken> | null = parseJwtPayload(accessToken);
  if (!payload) {
    return false;
  }

  return Boolean(payload.crmClientNumber || payload.anonymousPassportId);
}

/** Fallback TTL when verify returns session without jwt body (AGW Set-Cookie only). */
export const DEFAULT_PASSPORT_ACCESS_TTL_SEC: number = 300;
